/**
 * Danger Zone — red-bordered card at the bottom of TikTok Listing detail page.
 * Contains delete button with double-confirmation dialog (type product title).
 * After deletion: redirect to TikTok Listings list with Thai toast.
 *
 * Hidden when product is already deleted, frozen, or platform-deactivated.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { useTikTokDelete } from "../hooks/useTikTokLifecycle";
import { useTikTokEditStore, selectIsReadOnly } from "../stores/useTikTokEditStore";

export function TikTokDangerZoneWidget({ section, formData }: ExperienceWidgetProps) {
  const isTikTok = (section.description || "") === "TikTok Shop";
  const isReadOnly = useTikTokEditStore(selectIsReadOnly);
  const status = (formData?.status as string || "").toLowerCase();

  // Don't show danger zone for already-deleted, read-only, or non-TikTok
  if (!isTikTok || status === "deleted" || isReadOnly) return null;

  return <DangerZoneContent formData={formData} />;
}

function DangerZoneContent({ formData }: { formData: Record<string, any> }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const navigate = useNavigate();
  const deleteMutation = useTikTokDelete();

  const title = (formData?.title as string) || "";
  const canConfirm = confirmText.trim() === title.trim();

  const handleDelete = () => {
    deleteMutation.mutate(undefined, {
      onSuccess() {
        setOpen(false);
        toast.success("สินค้าถูกลบจาก TikTok แล้ว");
        navigate("/channels/tiktok-shop");
      },
      onError(err) {
        toast.error(err?.message || "ลบสินค้าไม่สำเร็จ");
      },
    });
  };

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-red-500 uppercase tracking-wide pl-4 mb-2">
        Danger Zone
      </h3>
      <div className="rounded-2xl overflow-hidden border border-red-200 bg-card">
        <div className="flex items-center justify-between px-4 py-5">
          <div>
            <p className="text-lg font-medium text-red-700">ลบสินค้าจาก TikTok</p>
            <p className="text-sm text-muted-foreground mt-0.5">การลบจะไม่สามารถกู้คืนได้</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => setOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            ลบ
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirmText(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ยืนยันการลบสินค้า</DialogTitle>
            <DialogDescription>
              การลบสินค้านี้จาก TikTok จะไม่สามารถกู้คืนได้ พิมพ์ชื่อสินค้าเพื่อยืนยัน:
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm font-medium text-foreground mb-2 break-words">{title}</p>
            <input
              type="text"
              className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-400/40"
              placeholder="พิมพ์ชื่อสินค้าที่นี่"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setConfirmText(""); }}>
              ยกเลิก
            </Button>
            <Button
              variant="destructive"
              disabled={!canConfirm || deleteMutation.isPending}
              onClick={handleDelete}
            >
              {deleteMutation.isPending ? "กำลังลบ..." : "ลบถาวร"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
